import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, RadioGroup, Radio, Label, TextField, TextArea } from '@heroui/react';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { emitAppEvent, onAppEvent, type ReportPayload } from '@/lib/appEvents';
import type { ReportReason } from '@bunker/shared';

/**
 * "Пожаловаться" — file a report on a message or a user.
 *
 * Mounted once app-wide (next to ForwardModal in HeroApp); opens via the typed
 * `report:open` app event so openers don't prop-drill modal state. The server
 * derives scope / guild / author / content snapshot from the target id — the
 * modal only collects the reason + an optional note, then calls api.createReport.
 *
 * Store-compliance: App Store / Google Play require a way to report
 * objectionable content and the users posting it.
 */

/** The reason list, in display order. `csam` is intentionally the most severe;
 *  it (plus violence / illegal / self_harm) escalates to the platform queue
 *  server-side regardless of where the content lives. */
const REASONS: ReportReason[] = [
  'spam',
  'harassment',
  'nsfw',
  'violence',
  'self_harm',
  'csam',
  'illegal',
  'other',
];

export function ReportModal() {
  const { t } = useTranslation();
  const [payload, setPayload] = useState<ReportPayload | null>(null);
  const [reason, setReason] = useState<ReportReason>('spam');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const open = payload !== null;

  useEffect(() => {
    return onAppEvent('report:open', (detail) => {
      if (!detail) return;
      setReason('spam');
      setNote('');
      setPayload(detail);
    });
  }, []);

  function close() {
    if (sending) return;
    setPayload(null);
  }

  async function submit() {
    if (!payload || sending) return;
    setSending(true);
    try {
      const trimmed = note.trim();
      await api.createReport(
        payload.kind === 'message'
          ? { kind: 'message', messageId: payload.targetId, reason, ...(trimmed ? { note: trimmed } : {}) }
          : { kind: 'user', userId: payload.targetId, reason, ...(trimmed ? { note: trimmed } : {}) },
      );
      toast.success(t('report.sent'));
      setPayload(null);
    } catch (err) {
      toast.error(...errToToast(err, t('report.error')));
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal.Backdrop isOpen={open} onOpenChange={(o) => !o && close()}>
      {/* close() already no-ops while `sending`, so Escape/backdrop can't
          dismiss mid-submit (no state-update-on-unmounted). */}
      <Modal.Container scroll="inside" size="sm">
        <Modal.Dialog aria-label={t('report.title')}>
          <Modal.Header>
            <div className="flex min-w-0 flex-col gap-0.5">
              <span>{t('report.title')}</span>
              {payload && (
                <span className="block truncate text-xs font-normal text-ink-muted">
                  {payload.kind === 'message'
                    ? t('report.subtitleMessage', { name: payload.targetName })
                    : t('report.subtitleUser', { name: payload.targetName })}
                </span>
              )}
            </div>
          </Modal.Header>
          <Modal.Body className="flex flex-col gap-4">
            <RadioGroup
              variant="secondary"
              aria-label={t('report.reasonLabel')}
              value={reason}
              onChange={(v) => setReason(v as ReportReason)}
            >
              {REASONS.map((r) => (
                <Radio key={r} value={r}>
                  <Radio.Control>
                    <Radio.Indicator />
                  </Radio.Control>
                  <Radio.Content>
                    <Label>{t(`report.reason.${r}`)}</Label>
                  </Radio.Content>
                </Radio>
              ))}
            </RadioGroup>
            <TextField fullWidth aria-label={t('report.noteLabel')}>
              <TextArea
                variant="secondary"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder={t('report.notePlaceholder')}
              />
            </TextField>
            <p className="text-[11px] leading-relaxed text-ink-muted">{t('report.privacyHint')}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" onPress={close} isDisabled={sending}>
              {t('report.cancel')}
            </Button>
            <Button variant="primary" onPress={() => void submit()} isPending={sending}>
              {t('report.submit')}
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

/** Imperative opener — emits the typed app event (no prop-drilling). */
export function openReportModal(payload: ReportPayload): void {
  emitAppEvent('report:open', payload);
}
